import React, { useEffect, useState } from 'react';
import apiClient from '../utils/api';
import { formatDate } from '../utils/dateUtils';
import { getTeamLogoUrl } from '../utils/logoUrl';
import ScoredMatchSummary from './ScoredMatchSummary';
import {
  getPredictionOutcome,
  getOutcomeCardClasses,
  getOutcomeAccentColor,
  getOutcomeMutedTextColor,
  hasMatchResult,
} from '../utils/predictionOutcomeUtils';

interface RecentPrediction {
  id: string;
  homeGoals: number;
  awayGoals: number;
  isBanker: boolean;
  points: number;
  isScored: boolean;
  match: {
    id: string;
    matchDate: string;
    homeGoals: number | null;
    awayGoals: number | null;
    status: string;
    homeTeam: { name: string; logoUrl?: string | null };
    awayTeam: { name: string; logoUrl?: string | null };
  };
  season: { id: string; name: string };
  matchGroup?: { name: string; matchWeek?: number | null };
}

interface LatestPredictionsProps {
  userId: string;
  limit?: number;
}

const LatestPredictions: React.FC<LatestPredictionsProps> = ({ userId, limit = 20 }) => {
  const [predictions, setPredictions] = useState<RecentPrediction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/users/${userId}/predictions/recent`, {
          params: { limit },
        });
        setPredictions(response.data);
      } catch (error) {
        console.error('Failed to fetch recent predictions:', error);
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchRecent();
    }
  }, [userId, limit]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="card rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (predictions.length === 0) {
    return (
      <div className="card rounded-lg p-6 text-center" style={{ color: 'var(--text-secondary)' }}>
        No scored predictions yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {predictions.map(prediction => {
        const { match } = prediction;
        const matchHasResult = hasMatchResult(match.homeGoals, match.awayGoals);
        const outcome = getPredictionOutcome(
          match.homeGoals,
          match.awayGoals,
          prediction,
          false
        );
        const accentClass = getOutcomeAccentColor(outcome);
        const mutedClass = getOutcomeMutedTextColor(outcome);
        const scoredOutcome = outcome === 'exact' || outcome === 'correct' || outcome === 'wrong';

        return (
          <div
            key={prediction.id}
            className={`card rounded-lg overflow-hidden p-4 ${getOutcomeCardClasses(outcome, true)}`}
          >
            <div className={`flex flex-wrap items-center justify-between gap-2 text-xs mb-4 ${mutedClass}`}>
              <span className="font-semibold">{prediction.season.name}</span>
              <span>{prediction.matchGroup?.name || formatDate(match.matchDate)}</span>
            </div>

            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 mb-2">
              <div className="text-center min-w-0">
                {match.homeTeam.logoUrl && (
                  <img
                    src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                    alt=""
                    className="w-8 h-8 object-contain mx-auto mb-1"
                  />
                )}
                <div className={`text-xs sm:text-sm font-semibold break-words ${accentClass}`}>
                  {match.homeTeam.name}
                </div>
              </div>

              <div className="text-center px-2 min-w-[4.5rem]">
                {matchHasResult ? (
                  <>
                    <div className={`text-xl sm:text-2xl font-bold tabular-nums ${accentClass}`}>
                      {match.homeGoals} - {match.awayGoals}
                    </div>
                    <div className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${mutedClass}`}>
                      Full time
                    </div>
                  </>
                ) : (
                  <div className={`text-sm font-medium ${mutedClass}`}>vs</div>
                )}
              </div>

              <div className="text-center min-w-0">
                {match.awayTeam.logoUrl && (
                  <img
                    src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                    alt=""
                    className="w-8 h-8 object-contain mx-auto mb-1"
                  />
                )}
                <div className={`text-xs sm:text-sm font-semibold break-words ${accentClass}`}>
                  {match.awayTeam.name}
                </div>
              </div>
            </div>

            {scoredOutcome && (
              <ScoredMatchSummary
                prediction={prediction}
                outcome={outcome as 'exact' | 'correct' | 'wrong'}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LatestPredictions;
